import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { IComplaint } from 'app/models/complaint/complaint';
import { CommonDataService } from 'app/services/common/common-data.service';
import { ComplaintService } from 'app/services/complaint/complaint.service';
import { SharedService } from 'app/services/core/shared.service';
import { AddEditComplaintComponent } from './add-edit-complaint/add-edit-complaint.component';
import { LEAD_BASED_PAINT } from '@shared/utils/app-static-data';
import { IComplaintFilter } from 'app/models/filters/complaintFilter';
import { ToastrService } from 'ngx-toastr';


@Component({
  selector: 'app-complaint',
  templateUrl: './complaint.component.html',
  styleUrls: ['./complaint.component.scss']
})
export class ComplaintComponent {
    public isMobile: boolean = false;
    private readonly complaintStatuses: string[] = ['Submitted', 'Open', 'Closed'];

    statusColor(status: string): string {
      return this.sharedService.getStatusColor(status);
    }
    public loading: boolean = true;
    public pageSize: number = 50;  
    public complaints: IComplaint[] = [];
    public dataSource!: MatTableDataSource<any>;
    public filterString: string = '';
    public filterAddress: string = '';
    public filteredComplaints: IComplaint[] = [];
    public filteredStatus = new FormControl(['Open']);
    public filteredComplaintType = new FormControl('All');
    public uiData = LEAD_BASED_PAINT;

    private readonly filter: IComplaintFilter = {
      filterString: '',
      filterAddress: '',
      filterStatus: 'Open',
      filterType: 'All'      
    }

    @ViewChild('TablePaginator') paginator!: MatPaginator;
    @ViewChild('TableSort') sort!: MatSort;
    
    complaintsTable = {
      icon: 'recent_actors',
      title:'Complaints',
      layout:{
        columns:['id', 'complainteeName', 'complainantName', 'complainantAddress', 'status', 'complaintDate', 'lastUpdatedBy'],
        container:[
          {displayName:'Id',columnName:'id', type:'string', size:'5'},
          {displayName:'Complaint Source Name',columnName:'complainteeName', type:'string', size:'20'},
          {displayName:'Complaint Target Name',columnName:'complainantName', type:'string', size:'20'},
          {displayName:'Complaint Location',columnName:'complainantAddress', type:'string', size:'15'},        
          {displayName:'Status',columnName:'status', type:'string', size:'10'},          
          {displayName:'Complaint Date',columnName:'complaintDate', type:'date', size:'8'},     
          {displayName:'Last Updated By',columnName:'lastUpdatedBy', type:'string', size:'15'}, 
        ],
        data: []      
      }
    }
  
  constructor(private readonly breakpointObserver: BreakpointObserver,
    public sharedService: SharedService,
    public dialog: MatDialog,
    public toastr: ToastrService,
    public commonService: CommonDataService,
    public complaintService: ComplaintService) 
  {
    this.breakpointObserver.observe([
        Breakpoints.Handset,
        Breakpoints.Tablet,
        Breakpoints.Small,
      ]).subscribe(result => {
        this.isMobile = result.matches;
      });
      this.loadComplaints();
  }

  loadComplaints(): void {
    this.filterString = this.commonService.complaintsFilter.filterString;
    this.filterAddress = this.commonService.complaintsFilter.filterAddress;
    this.filteredStatus = this.commonService.complaintsFilter.filteredStatus;
    this.filteredComplaintType = this.commonService.complaintsFilter.filteredComplaintType;
    this.normalizeStatusFilterValue();
    this.refreshComplaints();
  }

  refreshComplaints(){
    const selectedStatuses = this.getSelectedStatuses();
    this.filter.filterStatus = selectedStatuses.length === 1 ? selectedStatuses[0] : 'Both';
    this.filter.filterString = this.filterString;
    this.filter.filterAddress = this.filterAddress;
    this.filter.filterType = this.filteredComplaintType.value || 'All';

    this.complaintService.getComplaints(this.filter).subscribe(result=>{
      const selectedStatusSet = new Set(selectedStatuses.map(status => status.toLowerCase()));
      const filteredByStatus = selectedStatuses.length > 1
        ? result.filter(complaint => selectedStatusSet.has((complaint.status || '').toLowerCase()))
        : result;

      this.complaints = result;    
      this.filteredComplaints = filteredByStatus;
      this.updateDataSource();
      this.loading = false;
    },error=>{
      this.toastr.error('Error loading complaints:', error.error);
    })
  }

  updateDataSource(){
    this.dataSource = new MatTableDataSource<any>(this.filteredComplaints);
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }
  
  getComplaintStatuses(){
    return this.complaintStatuses;
  }

  getComplaintTypes(){
    return ['Atp','Firm','Individual','All'];
  }

  clearFilters(){
    this.filterString = '';    
    this.filterAddress = '';    
    this.filteredStatus.setValue(['Open']);
    this.filteredComplaintType.setValue('All');
    this.filterTable();
  }

  filterTable() { 
    this.commonService.complaintsFilter.filterString = this.filterString;
    this.commonService.complaintsFilter.filterAddress = this.filterAddress;
    this.commonService.complaintsFilter.filteredStatus = this.filteredStatus;
    this.commonService.complaintsFilter.filteredComplaintType = this.filteredComplaintType;
    
    this.refreshComplaints(); 
  }

  editCompliance(complaint: IComplaint) {    
    if(complaint != null && complaint.id > 0){
      this.complaintService.getComplaintById(complaint.id).subscribe(result=>{   
        const dialogRef = this.dialog.open(AddEditComplaintComponent, {
          width: '90%',
          data: {complaint : result },});
        dialogRef.afterClosed().subscribe(result => {  
          this.loadComplaints();    
        });      
      },error=>{  
      });   
      }else{ const dialogRef = this.dialog.open(AddEditComplaintComponent, {
        width: '90%',
        data: {complaint : null },});
      dialogRef.afterClosed().subscribe(result => {  
        this.loadComplaints();    
      });
    }   
  }

  private normalizeStatusFilterValue(): void {
    const value = this.filteredStatus.value as unknown;

    if (Array.isArray(value)) {
      const selectedStatuses = value
        .filter((status): status is string => typeof status === 'string')
        .map(status => status.trim())
        .filter(Boolean);

      if (selectedStatuses.length !== value.length) {
        this.filteredStatus.setValue(selectedStatuses);
      }

      return;
    }

    if (typeof value === 'string' && value.trim()) {
      if (value === 'Both') {
        this.filteredStatus.setValue([...this.complaintStatuses]);
        return;
      }

      const parsed = value.split(',').map((v: string) => v.trim()).filter(Boolean);
      this.filteredStatus.setValue(parsed.length > 0 ? parsed : ['Open']);
      return;
    }

    this.filteredStatus.setValue(['Open']);
  }

  private getSelectedStatuses(): string[] {
    const value = this.filteredStatus.value as unknown;

    return Array.isArray(value)
      ? value
        .filter((status): status is string => typeof status === 'string')
        .map(status => status.trim())
        .filter(Boolean)
      : typeof value === 'string' && value.trim()
        ? [value.trim()]
        : [];
  }
}
